// Libs
import classNames from 'classnames/bind';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { NoteItem } from '~/components/module/note';
import { FormNoteEstimate } from '~/components/module/estimate';
import { BaseToolbar, ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
// Others
import { LoadingContext } from '~/context';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
} from '~/utils/constants/common';
import { INote, IQueryGetListNote } from '~/utils/interface/note';
import { ParamsBusinessTypeEnum } from '~/utils/enum/common';
import { deleteNote, getListNote } from '~/thunks/notes/notesThunk';
import { notesActions, selectIsRefreshNote } from '~/thunks/notes/notesSlice';
// Styles, images, icons
import styles from './EstimateNotes.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const EstimateNote = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshNote = useSelector(selectIsRefreshNote);
  const loadingContext = useContext(LoadingContext);
  //#endregion Selector

  //#region Declare State
  const [isShowFormNote, setIsShowFormNote] = useState<boolean>(false);
  const [noteSelected, setNoteSelected] = useState<INote | null>(null);
  const [notes, setNotes] = useState<INote[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowConfirmDeleteNote, setIsShowConfirmDeleteNote] = useState<boolean>(false);
  const [hasMoreNote] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const handleGetListNoteEstimate = useCallback((params: IQueryGetListNote) => {
    dispatch(getListNote(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        setNotes((prevState) => {
          const newData = responses?.filter(
            (note) => !prevState?.some((oldNote) => oldNote?.id === note?.id)
          );

          const updateListNote = [...prevState, ...newData];

          // setHasMoreNote(updateListNote?.length < totalItems);

          return updateListNote;
        });

        setNotes(Array.isArray(responses) ? responses : []);
      })
      .catch((error) => {
        // setHasMoreNote(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    if (!estimateId) return;

    const params: IQueryGetListNote = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: estimateId,
      businessType: ParamsBusinessTypeEnum.ESTIMATE,
    };

    setIsLoading(true);

    handleGetListNoteEstimate(params);

  }, [estimateId, handleGetListNoteEstimate]);

  useEffect(() => {
    if (!isRefreshNote || !estimateId) return;

    const params: IQueryGetListNote = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: estimateId,
      businessType: ParamsBusinessTypeEnum.ESTIMATE,
    };

    handleGetListNoteEstimate(params);
    dispatch(notesActions.setIsRefreshNote(false));

  }, [isRefreshNote, estimateId, handleGetListNoteEstimate, dispatch]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleCloseFormNote = () => {
    setIsShowFormNote(false);
    setNoteSelected(null);
  };

  const handleAddNote = () => {
    setIsShowFormNote(true);
  };

  const handleEditNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowFormNote(true);
  };

  const handleDeleteNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowConfirmDeleteNote(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDeleteNote(false);
    setNoteSelected(null);
  };

  const handleConfirmDelete = () => {
    if (!noteSelected?.id) return;

    loadingContext?.show();

    dispatch(deleteNote(noteSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(notesActions.setIsRefreshNote(true));
      })
      .catch((error) => {})
      .finally(() => {
        loadingContext?.hide();
        handleCloseConfirmDelete();
      });
  };

  const handleLoadMoreNote = () => {
    // TODO: Handle logic load more note
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='estimateNoteTab' className={cx('container')}>
      <BaseToolbar textAction={t('form_note_estimate_btn_add')} onAction={handleAddNote}></BaseToolbar>

      {!isLoading && (
        <Box component={'div'} id='scrollableNotes' className={cx('listNoteContainer')}>
          <InfiniteScroll
            dataLength={notes.length}
            next={handleLoadMoreNote}
            hasMore={hasMoreNote}
            loader={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                <CircularProgress size={14} />
              </Stack>
            }
            scrollableTarget={'scrollableNotes'}
          >
            <Box id='scrollableNotes' component={'div'} className={cx('listNote')}>
              {Array.isArray(notes) && notes?.length > DEFAULT_NUMBER_ZERO ? (
                notes?.map((note) => (
                  <NoteItem note={note} key={note?.id} onEdit={handleEditNote} onDelete={handleDeleteNote} />
                ))
              ) : (
                <Typography component={'span'} className={cx('noDataAvailable')}>
                  {t('common_no_data_available', { ns: 'common/base' })}
                </Typography>
              )}
            </Box>
          </InfiniteScroll>
        </Box>
      )}

      {isShowFormNote && (
        <FormNoteEstimate {...(noteSelected && { note: noteSelected })} onClose={handleCloseFormNote} />
      )}

      {isShowConfirmDeleteNote && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete_note', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default EstimateNote;
